import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const WebSocketContext = createContext(null);

const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000/ws';

function setFaviconBadge(count) {
  const S = 128; // high-res canvas, browser scales down to 32px — stays sharp
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');

  // Dark rounded-square background (like a real app icon)
  const r = 28;
  ctx.fillStyle = '#1e293b';
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(S - r, 0);
  ctx.quadraticCurveTo(S, 0, S, r);
  ctx.lineTo(S, S - r);
  ctx.quadraticCurveTo(S, S, S - r, S);
  ctx.lineTo(r, S);
  ctx.quadraticCurveTo(0, S, 0, S - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();

  // Bell emoji centered, shifted slightly down-left to leave top-right for badge
  ctx.font = '72px serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('🔔', 58, 72);

  // Tint bell white
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.fillRect(0, 0, S, S);
  ctx.globalCompositeOperation = 'source-over';

  if (count > 0) {
    const label = count > 99 ? '99+' : String(count);
    // Badge sits at top-right corner, half-overlapping the icon edge
    const bx = label.length === 1 ? 96 : 100;
    const by = 30;
    const bRadius = label.length === 1 ? 22 : 26;

    // White border ring
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(bx, by, bRadius + 4, 0, Math.PI * 2);
    ctx.fill();

    // Red fill
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.arc(bx, by, bRadius, 0, Math.PI * 2);
    ctx.fill();

    // White bold count
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${label.length === 1 ? 28 : 22}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, bx, by + 1);
  }

  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = canvas.toDataURL('image/png');
}

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const unreadRef = useRef(0);

  // Reset title and count when tab becomes active
  useEffect(() => {
    const handleVisible = () => {
      unreadRef.current = 0;
      setUnreadCount(0);
      setFaviconBadge(0);

      // Reconnect immediately if the connection dropped while backgrounded
      if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
        if (reconnectTimeout.current) {
          clearTimeout(reconnectTimeout.current);
          reconnectTimeout.current = null;
        }
        connect();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) handleVisible();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisible);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = () => {
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    try {
      ws.current = new WebSocket(WS_URL);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Ignore server heartbeat — its only job is keeping the tab's JS
          // event loop alive so Chrome doesn't freeze background tabs
          if (data.type === 'heartbeat') return;

          setLastMessage(data);

          // Update favicon badge when an alert arrives and the tab is not focused
          if (data.type === 'mt5_alert' && (document.hidden || !document.hasFocus())) {
            unreadRef.current += 1;
            setUnreadCount(unreadRef.current);
            setFaviconBadge(unreadRef.current);
          }
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);

        reconnectTimeout.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connect();
        }, 5000);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  };

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (ws.current) ws.current.close();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = (message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  };

  const value = {
    isConnected,
    lastMessage,
    unreadCount,
    sendMessage,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};
